import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PencilSimple, Clock, Trash } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isOwner } from "@/lib/permissions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

export default function WorkSessionsAdmin() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const owner = isOwner(user?.role);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [saving, setSaving] = useState(false);

  const sessions = useQuery({
    queryKey: ["work-sessions"],
    queryFn: async () => (await api.get("/work/sessions")).data,
  });
  const team = useQuery({
    queryKey: ["entity", "team"],
    queryFn: async () => (await api.get("/team")).data,
  });
  const machines = useQuery({
    queryKey: ["entity", "machines"],
    queryFn: async () => (await api.get("/machines")).data,
  });

  const rows = useMemo(() => (sessions.data || []).slice(0, 10), [sessions.data]);
  const nameOf = (list, id, field) => (list || []).find((x) => x.id === id)?.[field] || "—";

  const openEdit = (s) => {
    setEditing(s);
    setStart(toLocalInput(s.start_time));
    setEnd(toLocalInput(s.end_time));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { start_time: new Date(start).toISOString() };
      if (editing.end_time) payload.end_time = end ? new Date(end).toISOString() : null;
      await api.put(`/work/sessions/${editing.id}/times`, payload);
      toast.success("Times corrected");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["work-sessions"] });
      qc.invalidateQueries({ queryKey: ["work-approvals"] });
      qc.invalidateQueries({ queryKey: ["entity"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const clearSessions = async () => {
    setClearing(true);
    try {
      const { data } = await api.post("/work/sessions/clear", { session_ids: selected });
      toast.success(`${data.count} session${data.count === 1 ? "" : "s"} cleared`);
      setSelected([]);
      setClearOpen(false);
      qc.invalidateQueries({ queryKey: ["work-sessions"] });
      qc.invalidateQueries({ queryKey: ["work-approvals"] });
      qc.invalidateQueries({ queryKey: ["work-assignments"] });
      qc.invalidateQueries({ queryKey: ["entity"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setClearing(false);
    }
  };

  if (rows.length === 0) return null;

  return (
    <Card className="border-slate-200 shadow-none" data-testid="work-sessions-admin">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-100">
        <Clock className="h-5 w-5 text-slate-500" weight="bold" />
        <h3 className="font-semibold text-slate-900">Work Sessions — latest {rows.length}</h3>
        <span className="text-xs text-slate-400 ml-1">only managers can correct times</span>
        {owner && (
          <Button size="sm" variant="outline" className="ml-auto h-7 text-red-700 border-red-200 hover:bg-red-50"
            disabled={selected.length === 0} onClick={() => setClearOpen(true)} data-testid="sessions-clear-btn">
            <Trash className="h-4 w-4 mr-1" /> Clear{selected.length ? ` (${selected.length})` : ""}
          </Button>
        )}
      </div>
      <ul className="divide-y divide-slate-100">
        {rows.map((s) => (
          <li key={s.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-5 py-3"
            data-testid={`session-row-${s.code}`}>
            <span className="text-sm min-w-0 flex items-start gap-2">
              {owner && (
                <Checkbox className="mt-0.5" checked={selected.includes(s.id)} onCheckedChange={() => toggle(s.id)}
                  data-testid={`session-select-${s.code}`} />
              )}
              <span className="min-w-0">
              <span className="font-mono text-xs text-slate-500">{s.code}</span>{" "}
              <span className="text-slate-900">{nameOf(team.data, s.operator_id, "name")}</span> ·{" "}
              {nameOf(machines.data, s.machine_id, "machine_name")}
              <span className="block text-xs text-slate-500 mt-0.5">
                {fmt(s.start_time)} → {fmt(s.end_time)} · {s.actual_hours} h
                {s.time_edited_by && <span className="text-amber-700"> · corrected by {s.time_edited_by}</span>}
              </span>
              </span>
            </span>
            <span className="flex items-center gap-2 shrink-0">
              <Badge variant="outline" className={
                s.status === "Completed" ? "bg-slate-100 text-slate-600"
                  : s.status === "Paused" ? "bg-amber-100 text-amber-700 border-amber-200"
                  : "bg-emerald-100 text-emerald-700 border-emerald-200"
              }>{s.status}</Badge>
              <Button size="sm" variant="outline" className="h-7" onClick={() => openEdit(s)}
                data-testid={`session-edit-${s.code}`}>
                <PencilSimple className="h-4 w-4 mr-1" /> Times
              </Button>
            </span>
          </li>
        ))}
      </ul>

      <Dialog open={clearOpen} onOpenChange={(v) => !v && setClearOpen(false)}>
        <DialogContent className="max-w-sm" data-testid="sessions-clear-dialog">
          <DialogHeader>
            <DialogTitle>Clear {selected.length} work session{selected.length === 1 ? "" : "s"}?</DialogTitle>
            <DialogDescription>
              The session and its auto-created production log are deleted, and machine utilization for those days is
              recalculated. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearOpen(false)} data-testid="sessions-clear-cancel">Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={clearSessions} disabled={clearing}
              data-testid="sessions-clear-confirm">{clearing ? "Clearing..." : "Clear sessions"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Correct session times</DialogTitle>
            <DialogDescription>
              {editing?.code} · hours are recalculated and the production log is updated. Operators cannot change these.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Start time</label>
              <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)}
                className="mt-1" data-testid="session-start-input" />
            </div>
            {editing?.end_time && (
              <div>
                <label className="text-xs font-medium text-slate-500">End time</label>
                <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)}
                  className="mt-1" data-testid="session-end-input" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="session-time-cancel">Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} disabled={saving}
              data-testid="session-time-save">{saving ? "Saving..." : "Save times"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
